import React, { useState, useRef } from 'react';
import { X, Upload, Image as ImageIcon, Trash2, Loader2 } from 'lucide-react';

function ImageUploadModal({ 
  isOpen, 
  onClose, 
  onUpload, 
  onDelete,
  currentImage, 
  imageType, 
  loading 
}) {
  const [previewImage, setPreviewImage] = useState(currentImage);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const imageTypeLabels = {
    profile: 'Imagen de Perfil',
    background: 'Imagen de Fondo'
  };

  function handleFileSelect(e) {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
  }

  function processFile(file) {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Por favor selecciona una imagen válida');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('La imagen no debe superar los 5MB');
      return;
    }

    // Read file and convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewImage(reader.result);
    };
    reader.readAsDataURL(file);
  }

  function handleDragOver(e) {
    e.preventDefault();
    setIsDragging(true);
  }

  function handleDragLeave(e) {
    e.preventDefault();
    setIsDragging(false);
  }

  function handleDrop(e) {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  }

  async function handlePaste(e) {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          processFile(file);
        }
        break;
      }
    }
  }

  function handleUploadClick() {
    if (previewImage && previewImage !== currentImage) {
      onUpload(previewImage);
    }
  }

  function handleDeleteClick() {
    if (currentImage) {
      onDelete();
      setPreviewImage(null);
    }
  }

  function handleBackdropClick(e) {
    if (e.target === e.currentTarget && !loading) {
      onClose();
    }
  }

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-[#B1336E] text-white p-3 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <ImageIcon className="w-4 h-4" />
            <h2 className="text-base font-bold font-montserrat">
              {imageTypeLabels[imageType] || 'Subir Imagen'}
            </h2>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="p-1 hover:bg-white/20 rounded-lg transition-colors disabled:opacity-50"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          {/* Current/Preview Image */}
          <div className="relative">
            <label className="block text-xs font-semibold text-slate-700 mb-1.5 font-montserrat">
              {previewImage ? 'Vista Previa' : 'Sin imagen'}
            </label>
            <div className={`relative ${imageType === 'background' ? 'aspect-video' : 'aspect-square max-w-xs mx-auto'} bg-slate-100 rounded-lg overflow-hidden border-2 border-slate-200 max-h-48`}>
              {previewImage ? (
                <img
                  src={previewImage}
                  alt="Preview"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ImageIcon className="w-12 h-12 text-slate-300" />
                </div>
              )}
            </div>
          </div>

          {/* Upload Area */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onPaste={handlePaste}
            className={`border-2 border-dashed rounded-lg p-4 text-center transition-all ${
              isDragging 
                ? 'border-[#B1336E] bg-[#B1336E]/5' 
                : 'border-slate-300 hover:border-[#B1336E]/50'
            }`}
          >
            <Upload className={`w-8 h-8 mx-auto mb-2 ${isDragging ? 'text-[#B1336E]' : 'text-slate-400'}`} />
            <p className="text-xs font-semibold text-slate-700 mb-1 font-montserrat">
              Arrastra, pega o selecciona una imagen
            </p>
            <p className="text-[10px] text-slate-500 mb-3 font-montserrat">
              JPG, PNG o GIF (máx. 5MB)
            </p>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={loading}
              className="bg-[#B1336E] hover:bg-[#B1336E]/90 text-white px-4 py-1.5 rounded-lg text-sm font-semibold transition-all font-montserrat disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Seleccionar Archivo
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
            <p className="text-[10px] text-blue-700 font-montserrat leading-relaxed">
              💡 <strong>Sugerencia:</strong> {imageType === 'background' 
                ? 'Usa imágenes horizontales (16:9)'
                : 'Usa imágenes cuadradas'}
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-2 flex-shrink-0">
            {currentImage && (
              <button
                onClick={handleDeleteClick}
                disabled={loading}
                className="flex items-center gap-1.5 flex-1 bg-red-50 hover:bg-red-100 border-2 border-red-200 text-red-600 font-semibold py-2 px-3 rounded-lg transition-all font-montserrat text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span className="hidden sm:inline">Eliminando...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Eliminar</span>
                  </>
                )}
              </button>
            )}
            
            <button
              onClick={handleUploadClick}
              disabled={!previewImage || previewImage === currentImage || loading}
              className="flex items-center gap-1.5 flex-1 bg-[#67A43C] hover:bg-[#67A43C]/90 text-white font-semibold py-2 px-3 rounded-lg transition-all font-montserrat text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span className="hidden sm:inline">Subiendo...</span>
                </>
              ) : (
                <>
                  <Upload className="w-3.5 h-3.5" />
                  Subir
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ImageUploadModal;